import { Button } from '@/components/ui/button';
import { Download, FileSpreadsheet } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { DatosReporte } from '@/hooks/useReporteAmbiental';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface ExportarReporteProps {
  datos: DatosReporte;
  periodo?: string;
}

export const ExportarReporte = ({ datos, periodo = '2025' }: ExportarReporteProps) => {
  const { toast } = useToast();

  const exportarPDF = async () => {
    try {
      toast({
        title: 'Generando PDF',
        description: 'Por favor espera...',
      });

      const elemento = document.getElementById('reporte-ica');
      if (!elemento) {
        throw new Error('No se encontró el elemento del reporte');
      }

      const canvas = await html2canvas(elemento, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'letter',
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 0;

      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      pdf.save(`Reporte_ICA_${datos.obligacion.codigo}_${periodo}.pdf`);

      toast({
        title: 'PDF generado',
        description: 'El reporte se ha descargado correctamente',
      });
    } catch (error) {
      console.error('Error al generar PDF:', error);
      toast({
        title: 'Error',
        description: 'No se pudo generar el PDF',
        variant: 'destructive',
      });
    }
  };

  const exportarExcel = async () => {
    try {
      toast({
        title: 'Generando Excel',
        description: 'Por favor espera...',
      });

      const ExcelJS = (await import('exceljs')).default;
      const { obligacion, planManejo } = datos;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Reporte ICA');

      // ========== ESTRUCTURA DE 15 COLUMNAS (A-O) - Anchos exactos según formato ICA-1a ==========
      worksheet.getColumn(1).width = 4;     // A - N° (muy estrecha)
      worksheet.getColumn(2).width = 8;     // B - Logo/parte descripción
      worksheet.getColumn(3).width = 12;    // C - Descripción parte 2
      worksheet.getColumn(4).width = 12;    // D - Descripción parte 3
      worksheet.getColumn(5).width = 12;    // E - Descripción parte 4
      worksheet.getColumn(6).width = 12;    // F - Descripción parte 5
      worksheet.getColumn(7).width = 15;    // G - Descripción parámetro
      worksheet.getColumn(8).width = 15;    // H - Descripción parámetro parte 2
      worksheet.getColumn(9).width = 8;     // I - Valor parámetro
      worksheet.getColumn(10).width = 15;   // J - Descripción valor ref
      worksheet.getColumn(11).width = 10;   // K - Valor ref
      worksheet.getColumn(12).width = 5;    // L - Si parte 1
      worksheet.getColumn(13).width = 5;    // M - Si parte 2
      worksheet.getColumn(14).width = 5;    // N - No
      worksheet.getColumn(15).width = 5;    // O - Espacio adicional

      const fechaVersion = planManejo?.created_at 
        ? format(new Date(planManejo.created_at), 'dd/MM/yyyy', { locale: es })
        : 'N/A';

      const itemsMetas = (planManejo?.items_metas as any[]) || [];
      const itemsAcciones = (planManejo?.items_acciones as any[]) || [];
      const observacionesGenerales = planManejo?.observaciones_generales || '';

      const calcularPorcentajeCumplimiento = () => {
        if (itemsAcciones.length === 0) return 0;
        const porcentajes = itemsAcciones
          .map((item: any) => {
            const porcentaje = item.porcentaje_cumplimiento;
            if (typeof porcentaje === 'string') {
              return parseFloat(porcentaje.replace('%', '')) || 0;
            }
            return typeof porcentaje === 'number' ? porcentaje : 0;
          })
          .filter((p: number) => p > 0);
        if (porcentajes.length === 0) return 0;
        const suma = porcentajes.reduce((acc: number, val: number) => acc + val, 0);
        return Math.round(suma / porcentajes.length);
      };

      let currentRow = 1;

      // ========== FILAS 1-3: ENCABEZADO PRINCIPAL ==========
      
      // Logo ARGOS (A1:B3) - Merge vertical y horizontal
      worksheet.mergeCells('A1:B3');
      try {
        const logoResponse = await fetch('/src/assets/logo-argos.png');
        const logoBuffer = await logoResponse.arrayBuffer();
        const logoId = workbook.addImage({
          buffer: logoBuffer,
          extension: 'png',
        });
        worksheet.addImage(logoId, {
          tl: { col: 0, row: 0 } as any,
          ext: { width: 100, height: 45 } as any,
        });
      } catch (error) {
        console.error('Error al cargar el logo:', error);
      }
      const logoCell = worksheet.getCell('A1');
      logoCell.alignment = { vertical: 'middle', horizontal: 'center' };
      logoCell.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // Título principal (C1:K3) - El título grande va en el medio
      worksheet.mergeCells('C1:K3');
      const tituloCell = worksheet.getCell('C1');
      tituloCell.value = `ESTADO DE CUMPLIMIENTO DE LOS PROGRAMAS QUE CONFORMAN EL PLAN DE MANEJO AMBIENTAL DEL AEROPUERTO EL EMBRUJO PROVIDENCIA - PERIODO ${periodo}`;
      tituloCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      tituloCell.font = { bold: true, size: 10, name: 'Arial' };
      tituloCell.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // FORMATO: (L1:N1)
      worksheet.mergeCells('L1:N1');
      const formatoLabelCell = worksheet.getCell('L1');
      formatoLabelCell.value = 'FORMATO:';
      formatoLabelCell.alignment = { vertical: 'middle', horizontal: 'center' };
      formatoLabelCell.font = { bold: true, size: 9, name: 'Arial' };
      formatoLabelCell.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // Columna O vacía en fila 1
      worksheet.getCell('O1').border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // ICA-1a (L2:N2)
      worksheet.mergeCells('L2:N2');
      const formatoValueCell = worksheet.getCell('L2');
      formatoValueCell.value = 'ICA-1a';
      formatoValueCell.alignment = { vertical: 'middle', horizontal: 'center' };
      formatoValueCell.font = { bold: true, size: 10, name: 'Arial' };
      formatoValueCell.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // Columna O vacía en fila 2
      worksheet.getCell('O2').border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // Hoja 1 de 37 (L3:N3)
      worksheet.mergeCells('L3:N3');
      const hojaCell = worksheet.getCell('L3');
      hojaCell.value = 'Hoja 1 de 37';
      hojaCell.alignment = { vertical: 'middle', horizontal: 'center' };
      hojaCell.font = { size: 9, name: 'Arial' };
      hojaCell.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // Columna O vacía en fila 3
      worksheet.getCell('O3').border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      worksheet.getRow(1).height = 20;
      worksheet.getRow(2).height = 20;
      worksheet.getRow(3).height = 20;

      currentRow = 4;

      // ========== FILA 4: INFORMACIÓN DEL PROGRAMA ==========
      
      // PROGRAMA: (A4:K4)
      worksheet.mergeCells('A4:K4');
      const programaCell = worksheet.getCell('A4');
      programaCell.value = `PROGRAMA:  ${obligacion.nombre_programa || 'N/A'}`;
      programaCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
      programaCell.font = { size: 9, name: 'Arial' };
      programaCell.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // VERSIÓN/FECHA: (L4:M4)
      worksheet.mergeCells('L4:M4');
      const versionCell = worksheet.getCell('L4');
      versionCell.value = `VERSIÓN/FECHA: ${fechaVersion}`;
      versionCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      versionCell.font = { size: 8, bold: true, name: 'Arial' };
      versionCell.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // CÓDIGO: (N4:O4)
      worksheet.mergeCells('N4:O4');
      const codigoCell = worksheet.getCell('N4');
      codigoCell.value = `CÓDIGO: ${obligacion.codigo || 'N/A'}`;
      codigoCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      codigoCell.font = { size: 8, bold: true, name: 'Arial' };
      codigoCell.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      worksheet.getRow(currentRow).height = 35;
      currentRow++;

      // ========== FILA 5: TÍTULO SECCIÓN METAS ==========
      worksheet.mergeCells(currentRow, 1, currentRow, 15);
      const tituloMetas = worksheet.getCell(currentRow, 1);
      tituloMetas.value = 'CUMPLIMIENTO DE METAS (INDICADORES DE ÉXITO)';
      tituloMetas.alignment = { vertical: 'middle', horizontal: 'left' };
      tituloMetas.font = { bold: true, size: 10, name: 'Arial' };
      tituloMetas.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF808080' }
      };
      tituloMetas.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };
      worksheet.getRow(currentRow).height = 22;
      currentRow++;

      // ========== FILAS 6-7: ENCABEZADOS TABLA METAS ==========
      
      // Fila 6: Headers nivel 1
      const headerRow1 = currentRow;
      
      // 1. METAS (A6:B6)
      worksheet.mergeCells(headerRow1, 1, headerRow1, 2);
      const metasHeader = worksheet.getCell(headerRow1, 1);
      metasHeader.value = '1. METAS';
      metasHeader.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      metasHeader.font = { bold: true, size: 9, name: 'Arial' };
      metasHeader.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
      };
      metasHeader.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // Espacio vacío C6:F6 (para merge vertical posterior)
      for (let col = 3; col <= 6; col++) {
        const cell = worksheet.getCell(headerRow1, col);
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD3D3D3' }
        };
        cell.border = {
          top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
        };
      }

      // 2. PARÁMETRO DE CONTROL MEDIDO (G6:I6)
      worksheet.mergeCells(headerRow1, 7, headerRow1, 9);
      const parametroHeader = worksheet.getCell(headerRow1, 7);
      parametroHeader.value = '2. PARÁMETRO DE CONTROL MEDIDO';
      parametroHeader.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      parametroHeader.font = { bold: true, size: 9, name: 'Arial' };
      parametroHeader.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
      };
      parametroHeader.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // 3. VALOR DE REFERENCIA O CARACTERÍSTICA DE CALIDAD (J6:K6)
      worksheet.mergeCells(headerRow1, 10, headerRow1, 11);
      const valorRefHeader = worksheet.getCell(headerRow1, 10);
      valorRefHeader.value = '3. VALOR DE REFERENCIA O CARACTERÍSTICA DE CALIDAD';
      valorRefHeader.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      valorRefHeader.font = { bold: true, size: 9, name: 'Arial' };
      valorRefHeader.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
      };
      valorRefHeader.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // 4. CUMPLIMIENTO (L6:O6)
      worksheet.mergeCells(headerRow1, 12, headerRow1, 15);
      const cumplimientoHeader = worksheet.getCell(headerRow1, 12);
      cumplimientoHeader.value = '4. CUMPLIMIENTO';
      cumplimientoHeader.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cumplimientoHeader.font = { bold: true, size: 9, name: 'Arial' };
      cumplimientoHeader.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
      };
      cumplimientoHeader.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      worksheet.getRow(headerRow1).height = 35;
      currentRow++;

      // Fila 7: Headers nivel 2
      const headerRow2 = currentRow;

      // N° (A7)
      const nHeader = worksheet.getCell(headerRow2, 1);
      nHeader.value = 'Nº';
      nHeader.alignment = { vertical: 'middle', horizontal: 'center' };
      nHeader.font = { bold: true, size: 8, name: 'Arial' };
      nHeader.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8E8E8' }
      };
      nHeader.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // Descripción (B7:F7)
      worksheet.mergeCells(headerRow2, 2, headerRow2, 6);
      const descMetasHeader = worksheet.getCell(headerRow2, 2);
      descMetasHeader.value = 'Descripción';
      descMetasHeader.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      descMetasHeader.font = { bold: true, size: 8, name: 'Arial' };
      descMetasHeader.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8E8E8' }
      };
      descMetasHeader.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // Descripción parámetro (G7:H7)
      worksheet.mergeCells(headerRow2, 7, headerRow2, 8);
      const descParamHeader = worksheet.getCell(headerRow2, 7);
      descParamHeader.value = 'Descripción';
      descParamHeader.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      descParamHeader.font = { bold: true, size: 8, name: 'Arial' };
      descParamHeader.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8E8E8' }
      };
      descParamHeader.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // Valor (I7)
      const valorParamHeader = worksheet.getCell(headerRow2, 9);
      valorParamHeader.value = 'Valor';
      valorParamHeader.alignment = { vertical: 'middle', horizontal: 'center' };
      valorParamHeader.font = { bold: true, size: 8, name: 'Arial' };
      valorParamHeader.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8E8E8' }
      };
      valorParamHeader.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // Descripción valor ref (J7)
      const descValorRefHeader = worksheet.getCell(headerRow2, 10);
      descValorRefHeader.value = 'Descripción';
      descValorRefHeader.alignment = { vertical: 'middle', horizontal: 'center' };
      descValorRefHeader.font = { bold: true, size: 8, name: 'Arial' };
      descValorRefHeader.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8E8E8' }
      };
      descValorRefHeader.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // Valor ref (K7)
      const valorRefValorHeader = worksheet.getCell(headerRow2, 11);
      valorRefValorHeader.value = 'Valor';
      valorRefValorHeader.alignment = { vertical: 'middle', horizontal: 'center' };
      valorRefValorHeader.font = { bold: true, size: 8, name: 'Arial' };
      valorRefValorHeader.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8E8E8' }
      };
      valorRefValorHeader.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // Si (L7:M7)
      worksheet.mergeCells(headerRow2, 12, headerRow2, 13);
      const siHeader = worksheet.getCell(headerRow2, 12);
      siHeader.value = 'Si';
      siHeader.alignment = { vertical: 'middle', horizontal: 'center' };
      siHeader.font = { bold: true, size: 8, name: 'Arial' };
      siHeader.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8E8E8' }
      };
      siHeader.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // No (N7:O7)
      worksheet.mergeCells(headerRow2, 14, headerRow2, 15);
      const noHeader = worksheet.getCell(headerRow2, 14);
      noHeader.value = 'No';
      noHeader.alignment = { vertical: 'middle', horizontal: 'center' };
      noHeader.font = { bold: true, size: 8, name: 'Arial' };
      noHeader.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8E8E8' }
      };
      noHeader.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      worksheet.getRow(headerRow2).height = 28;
      currentRow++;

      // ========== DATOS DE METAS ==========
      if (itemsMetas.length > 0) {
        itemsMetas.forEach((item: any, index: number) => {
          const parametrosDescripciones = item.parametro_control_descripciones || [];
          const parametrosValores = item.parametro_control_valores || [];
          const valoresDescripciones = item.valor_referencia_descripciones || [];
          const valoresReferencia = item.valor_referencia_valores || [];
          
          const maxFilas = Math.max(
            parametrosDescripciones.length || 0,
            valoresDescripciones.length || 0,
            1
          );

          const valorParametroUnico = Array.isArray(parametrosValores) && parametrosValores.length > 0
            ? parametrosValores[0] 
            : '';

          const impactosIds = item.impactos_ids || item.impactos_atendidos_ids || [];
          const numeracionImpactos = impactosIds.length > 0 
            ? Array.from({ length: impactosIds.length }, (_, i) => i + 1).join(', ')
            : (index + 1).toString();

          const startRow = currentRow;

          for (let filaIndex = 0; filaIndex < maxFilas; filaIndex++) {
            const row = worksheet.getRow(currentRow);

            // Primera fila del grupo
            if (filaIndex === 0) {
              // Número (A)
              row.getCell(1).value = numeracionImpactos;
              row.getCell(1).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
              row.getCell(1).font = { size: 8, name: 'Arial' };
              row.getCell(1).border = {
                top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
              };

              // Descripción meta (B:F)
              row.getCell(2).value = item.meta_descripcion || 'Sin descripción';
              row.getCell(2).alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
              row.getCell(2).font = { size: 8, name: 'Arial' };
              row.getCell(2).border = {
                top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
              };

              // Valor parámetro (I)
              row.getCell(9).value = valorParametroUnico;
              row.getCell(9).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
              row.getCell(9).font = { size: 8, name: 'Arial' };
              row.getCell(9).border = {
                top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
              };

              // Cumplimiento SI (L:M)
              row.getCell(12).value = item.cumplimiento_si_no === 'SI' ? 'X' : '';
              row.getCell(12).alignment = { vertical: 'middle', horizontal: 'center' };
              row.getCell(12).font = { size: 8, name: 'Arial' };
              row.getCell(12).border = {
                top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
              };

              // Cumplimiento NO (N:O)
              row.getCell(14).value = item.cumplimiento_si_no === 'NO' ? 'X' : '';
              row.getCell(14).alignment = { vertical: 'middle', horizontal: 'center' };
              row.getCell(14).font = { size: 8, name: 'Arial' };
              row.getCell(14).border = {
                top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
              };
            }

            // Descripción parámetro (G:H)
            row.getCell(7).value = parametrosDescripciones[filaIndex] || '';
            row.getCell(7).alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
            row.getCell(7).font = { size: 8, name: 'Arial' };
            row.getCell(7).border = {
              top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
            };

            // Descripción valor ref (J)
            row.getCell(10).value = valoresDescripciones[filaIndex] || '';
            row.getCell(10).alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
            row.getCell(10).font = { size: 8, name: 'Arial' };
            row.getCell(10).border = {
              top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
            };

            // Valor ref (K)
            row.getCell(11).value = valoresReferencia[filaIndex] || '';
            row.getCell(11).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            row.getCell(11).font = { size: 8, name: 'Arial' };
            row.getCell(11).border = {
              top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
            };

            row.height = Math.max(25, Math.ceil(
              Math.max(
                (parametrosDescripciones[filaIndex]?.length || 0) / 20,
                (valoresDescripciones[filaIndex]?.length || 0) / 20,
                (item.meta_descripcion?.length || 0) / 30
              ) * 15
            ));

            currentRow++;
          }

          // Merge vertical solo para: N°, Descripción Meta, Valor Parámetro, SI, NO
          // Las descripciones de parámetros y valores ref NO se fusionan verticalmente
          if (maxFilas > 1) {
            worksheet.mergeCells(startRow, 1, startRow + maxFilas - 1, 1);   // N°
            worksheet.mergeCells(startRow, 2, startRow + maxFilas - 1, 6);   // Descripción meta (B:F)
            worksheet.mergeCells(startRow, 9, startRow + maxFilas - 1, 9);   // Valor parámetro (I)
            worksheet.mergeCells(startRow, 12, startRow + maxFilas - 1, 13); // SI (L:M)
            worksheet.mergeCells(startRow, 14, startRow + maxFilas - 1, 15); // NO (N:O)
            
            // Merge horizontal G:H para cada fila individual de descripción parámetro
            for (let i = 0; i < maxFilas; i++) {
              worksheet.mergeCells(startRow + i, 7, startRow + i, 8);
            }
          } else {
            // Solo merge horizontal B:F, G:H, L:M, N:O
            worksheet.mergeCells(startRow, 2, startRow, 6);   // Descripción meta
            worksheet.mergeCells(startRow, 7, startRow, 8);   // Descripción parámetro
            worksheet.mergeCells(startRow, 12, startRow, 13); // SI
            worksheet.mergeCells(startRow, 14, startRow, 15); // NO
          }
        });
      } else {
        const row = worksheet.getRow(currentRow);
        row.getCell(1).value = '1';
        worksheet.mergeCells(currentRow, 2, currentRow, 6);
        row.getCell(2).value = 'Sin metas registradas';
        for (let col = 1; col <= 15; col++) {
          row.getCell(col).border = {
            top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
          };
        }
        currentRow++;
      }

      currentRow++;

      // ========== TABLA 2: CUMPLIMIENTO DE ACCIONES ==========
      
      worksheet.mergeCells(currentRow, 1, currentRow, 15);
      const tituloAcciones = worksheet.getCell(currentRow, 1);
      tituloAcciones.value = 'CUMPLIMIENTO DE LAS ACCIONES DEL PROYECTO (INDICADORES DE CUMPLIMIENTO)';
      tituloAcciones.alignment = { vertical: 'middle', horizontal: 'left' };
      tituloAcciones.font = { bold: true, size: 10, name: 'Arial' };
      tituloAcciones.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF808080' }
      };
      tituloAcciones.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };
      worksheet.getRow(currentRow).height = 22;
      currentRow++;

      // Encabezados nivel 1 acciones
      const accionesHeaderRow1 = currentRow;

      // 5. ACCIONES DE MANEJO (A:D)
      worksheet.mergeCells(accionesHeaderRow1, 1, accionesHeaderRow1, 4);
      const accionesManejo = worksheet.getCell(accionesHeaderRow1, 1);
      accionesManejo.value = '5. ACCIONES DE MANEJO, CORRECCIÓN O COMPENSACIÓN';
      accionesManejo.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      accionesManejo.font = { bold: true, size: 9, name: 'Arial' };
      accionesManejo.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
      };
      accionesManejo.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // 6. ACCIONES DE VERIFICACIÓN PERIÓDICA (E:F)
      worksheet.mergeCells(accionesHeaderRow1, 5, accionesHeaderRow1, 6);
      const verificacionPeriodica = worksheet.getCell(accionesHeaderRow1, 5);
      verificacionPeriodica.value = '6. ACCIONES DE VERIFICACIÓN PERIÓDICA';
      verificacionPeriodica.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      verificacionPeriodica.font = { bold: true, size: 9, name: 'Arial' };
      verificacionPeriodica.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
      };
      verificacionPeriodica.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // 7. ACCIONES DE VERIFICACIÓN SEGÚN AVANCE (G:H)
      worksheet.mergeCells(accionesHeaderRow1, 7, accionesHeaderRow1, 8);
      const verificacionAvance = worksheet.getCell(accionesHeaderRow1, 7);
      verificacionAvance.value = '7. ACCIONES DE VERIFICACION SEGÚN AVANCE';
      verificacionAvance.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      verificacionAvance.font = { bold: true, size: 9, name: 'Arial' };
      verificacionAvance.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
      };
      verificacionAvance.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // 8. OBSERVACIONES (I:O)
      worksheet.mergeCells(accionesHeaderRow1, 9, accionesHeaderRow1, 15);
      const observacionesHeader = worksheet.getCell(accionesHeaderRow1, 9);
      observacionesHeader.value = '8. OBSERVACIONES';
      observacionesHeader.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      observacionesHeader.font = { bold: true, size: 9, name: 'Arial' };
      observacionesHeader.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
      };
      observacionesHeader.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      worksheet.getRow(accionesHeaderRow1).height = 35;
      currentRow++;

      // Encabezados nivel 2 acciones
      const accionesHeaderRow2 = currentRow;

      // N.º (A)
      const nAccionesHeader = worksheet.getCell(accionesHeaderRow2, 1);
      nAccionesHeader.value = 'N.º';
      nAccionesHeader.alignment = { vertical: 'middle', horizontal: 'center' };
      nAccionesHeader.font = { bold: true, size: 8, name: 'Arial' };
      nAccionesHeader.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8E8E8' }
      };
      nAccionesHeader.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // Descripción (B:D)
      worksheet.mergeCells(accionesHeaderRow2, 2, accionesHeaderRow2, 4);
      const descAccionesHeader = worksheet.getCell(accionesHeaderRow2, 2);
      descAccionesHeader.value = 'Descripción';
      descAccionesHeader.alignment = { vertical: 'middle', horizontal: 'center' };
      descAccionesHeader.font = { bold: true, size: 8, name: 'Arial' };
      descAccionesHeader.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8E8E8' }
      };
      descAccionesHeader.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // Periodicidad de la verificación (E)
      const periodicidadHeader = worksheet.getCell(accionesHeaderRow2, 5);
      periodicidadHeader.value = 'Periodicidad de la verificación';
      periodicidadHeader.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      periodicidadHeader.font = { bold: true, size: 8, name: 'Arial' };
      periodicidadHeader.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8E8E8' }
      };
      periodicidadHeader.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // % de cumplimiento (F)
      const cumplimientoPercHeader = worksheet.getCell(accionesHeaderRow2, 6);
      cumplimientoPercHeader.value = '% de cumplimiento';
      cumplimientoPercHeader.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cumplimientoPercHeader.font = { bold: true, size: 8, name: 'Arial' };
      cumplimientoPercHeader.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8E8E8' }
      };
      cumplimientoPercHeader.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // % de avance programado (G)
      const avanceProgramadoHeader = worksheet.getCell(accionesHeaderRow2, 7);
      avanceProgramadoHeader.value = '% de avance programado';
      avanceProgramadoHeader.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      avanceProgramadoHeader.font = { bold: true, size: 8, name: 'Arial' };
      avanceProgramadoHeader.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8E8E8' }
      };
      avanceProgramadoHeader.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // % de avance a la fecha (H)
      const avanceFechaHeader = worksheet.getCell(accionesHeaderRow2, 8);
      avanceFechaHeader.value = '% de avance a la fecha';
      avanceFechaHeader.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      avanceFechaHeader.font = { bold: true, size: 8, name: 'Arial' };
      avanceFechaHeader.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8E8E8' }
      };
      avanceFechaHeader.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // Observaciones vacío (I:O)
      worksheet.mergeCells(accionesHeaderRow2, 9, accionesHeaderRow2, 15);
      const obsAccionesHeader = worksheet.getCell(accionesHeaderRow2, 9);
      obsAccionesHeader.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8E8E8' }
      };
      obsAccionesHeader.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      worksheet.getRow(accionesHeaderRow2).height = 28;
      currentRow++;

      // ========== DATOS DE ACCIONES ==========
      if (itemsAcciones.length > 0) {
        itemsAcciones.forEach((item: any, index: number) => {
          const row = worksheet.getRow(currentRow);
          
          // N° (A)
          row.getCell(1).value = index + 1;
          row.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
          row.getCell(1).font = { size: 8, name: 'Arial' };
          row.getCell(1).border = {
            top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
          };

          // Descripción (B:D)
          worksheet.mergeCells(currentRow, 2, currentRow, 4);
          row.getCell(2).value = item.accion_descripcion || '';
          row.getCell(2).alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
          row.getCell(2).font = { size: 8, name: 'Arial' };
          row.getCell(2).border = {
            top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
          };

          // Periodicidad (E)
          row.getCell(5).value = item.periodicidad_verificacion || '';
          row.getCell(5).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
          row.getCell(5).font = { size: 8, name: 'Arial' };
          row.getCell(5).border = {
            top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
          };

          // % cumplimiento (F)
          row.getCell(6).value = `${item.porcentaje_cumplimiento || '0'}${item.porcentaje_cumplimiento && !item.porcentaje_cumplimiento.toString().includes('%') ? '%' : ''}`;
          row.getCell(6).alignment = { vertical: 'middle', horizontal: 'center' };
          row.getCell(6).font = { size: 8, name: 'Arial' };
          row.getCell(6).border = {
            top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
          };

          // % avance programado (G)
          row.getCell(7).value = `${item.porcentaje_avance_programado || '0'}${item.porcentaje_avance_programado && !item.porcentaje_avance_programado.toString().includes('%') ? '%' : ''}`;
          row.getCell(7).alignment = { vertical: 'middle', horizontal: 'center' };
          row.getCell(7).font = { size: 8, name: 'Arial' };
          row.getCell(7).border = {
            top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
          };

          // % avance a la fecha (H)
          row.getCell(8).value = `${item.porcentaje_avance_fecha || '0'}${item.porcentaje_avance_fecha && !item.porcentaje_avance_fecha.toString().includes('%') ? '%' : ''}`;
          row.getCell(8).alignment = { vertical: 'middle', horizontal: 'center' };
          row.getCell(8).font = { size: 8, name: 'Arial' };
          row.getCell(8).border = {
            top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
          };

          // Observaciones (I:O)
          worksheet.mergeCells(currentRow, 9, currentRow, 15);
          row.getCell(9).value = item.observaciones || '';
          row.getCell(9).alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
          row.getCell(9).font = { size: 8, name: 'Arial' };
          row.getCell(9).border = {
            top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
          };

          row.height = Math.max(25, Math.ceil(Math.max(
            (item.accion_descripcion || '').length / 30,
            (item.observaciones || '').length / 35
          ) * 15));

          currentRow++;
        });
      } else {
        const row = worksheet.getRow(currentRow);
        row.getCell(1).value = '1';
        worksheet.mergeCells(currentRow, 2, currentRow, 4);
        row.getCell(2).value = 'Sin acciones registradas';
        worksheet.mergeCells(currentRow, 9, currentRow, 15);
        for (let col = 1; col <= 15; col++) {
          row.getCell(col).border = {
            top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
          };
        }
        currentRow++;
      }

      currentRow++;

      // ========== 9. PORCENTAJE DE CUMPLIMIENTO Y OBSERVACIONES ==========
      
      // Label (A:H)
      worksheet.mergeCells(currentRow, 1, currentRow, 8);
      const porcentajeLabel = worksheet.getCell(currentRow, 1);
      porcentajeLabel.value = '9. PORCENTAJE DE CUMPLIMIENTO DEL PROGRAMA (%)';
      porcentajeLabel.alignment = { vertical: 'middle', horizontal: 'left' };
      porcentajeLabel.font = { bold: true, size: 10, name: 'Arial' };
      porcentajeLabel.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8E8E8' }
      };
      porcentajeLabel.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      // Valor (I:O)
      worksheet.mergeCells(currentRow, 9, currentRow, 15);
      const porcentajeValue = worksheet.getCell(currentRow, 9);
      porcentajeValue.value = `${calcularPorcentajeCumplimiento()}%`;
      porcentajeValue.alignment = { vertical: 'middle', horizontal: 'center' };
      porcentajeValue.font = { size: 12, bold: true, name: 'Arial' };
      porcentajeValue.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      worksheet.getRow(currentRow).height = 28;
      currentRow++;

      // Observaciones generales (A:H) y Profesional responsable (I:N)
      const obsStartRow = currentRow;
      const obsEndRow = currentRow + 5;

      worksheet.mergeCells(obsStartRow, 1, obsEndRow, 8);
      const obsCell = worksheet.getCell(obsStartRow, 1);
      obsCell.value = `Observaciones Generales:\n\n${observacionesGenerales}`;
      obsCell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
      obsCell.font = { size: 8, name: 'Arial' };
      obsCell.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      worksheet.mergeCells(obsStartRow, 9, obsEndRow, 15);
      const profCell = worksheet.getCell(obsStartRow, 9);
      profCell.value = 'PROFESIONAL RESPONSABLE\n\nNombre: _____________________\n\nUnidad Administrativa del Aeropuerto\n\nCoordinación Grupo de Gestión Ambiental y Control Fauna\n\nFirma: _____________________';
      profCell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
      profCell.font = { size: 8, name: 'Arial' };
      profCell.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };

      for (let i = 0; i < 6; i++) {
        worksheet.getRow(obsStartRow + i).height = 25;
      }

      currentRow = obsEndRow + 1;
      currentRow++;

      // Footer
      worksheet.mergeCells(currentRow, 1, currentRow, 15);
      const footerCell = worksheet.getCell(currentRow, 1);
      footerCell.value = `Generado el ${format(new Date(), "dd/MM/yyyy 'a las' HH:mm", { locale: es })}`;
      footerCell.alignment = { vertical: 'middle', horizontal: 'left' };
      footerCell.font = { size: 8, color: { argb: 'FF808080' }, name: 'Arial' };

      // Guardar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Reporte_ICA_${obligacion.codigo}_${periodo}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Excel generado',
        description: 'El reporte se ha descargado correctamente',
      });
    } catch (error) {
      console.error('Error al generar Excel:', error);
      toast({
        title: 'Error',
        description: 'No se pudo generar el archivo Excel',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex gap-4">
      <Button onClick={exportarPDF} className="gap-2">
        <Download className="h-4 w-4" />
        Exportar a PDF
      </Button>
      <Button onClick={exportarExcel} className="gap-2" variant="outline">
        <FileSpreadsheet className="h-4 w-4" />
        Exportar a Excel
      </Button>
    </div>
  );
};
